import { ChevronDown, EllipsisVertical, Pen, Pencil, Trash } from 'lucide-react';
import React, { useState, useEffect, useCallback } from 'react';
import { FiMessageSquare, FiPlus, FiEdit3, FiTrash2, FiX, FiSave } from 'react-icons/fi';
import { formatLastModified } from '../modules/NestedFeed';

const FileNotes = ({ file, onUpdateNoteCount }) => {
    const [notes, setNotes] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showAddNote, setShowAddNote] = useState(false);
    const [newNoteText, setNewNoteText] = useState('');
    const [editingNoteId, setEditingNoteId] = useState(null);
    const [editingText, setEditingText] = useState('');
    const [error, setError] = useState('');
    const [openMenuNoteId, setOpenMenuNoteId] = useState(null);
    const [notesMinimized, setNotesMinimized] = useState(true);

    const s3ObjectId = file.id;

    const fetchNotes = useCallback(async () => {
        if (!s3ObjectId) return;
        
        setIsLoading(true);
        setError('');
        
        try {
            const response = await fetch(`https://api.casedb.co/file-notes.php?s3_object_id=${s3ObjectId}`, { credentials: 'include' });
            const data = await response.json();
            
            if (data.success) {
                setNotes(data.notes || []);
            } else {
                setError(data.error || 'Failed to load notes');
            }
        } catch (err) {
            setError('Error loading notes: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    }, [s3ObjectId]);

    useEffect(() => {
        fetchNotes();
    }, [fetchNotes]);

    const handleAddNote = async () => {
        if (!newNoteText.trim()) return;
        
        setIsLoading(true);
        setError('');
        
        try {
            const response = await fetch('https://api.casedb.co/file-notes.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    s3_object_id: s3ObjectId,
                    note_text: newNoteText.trim()
                }),
                credentials: 'include'
            });
            
            const data = await response.json();
            
            if (data.success) {
                setNotes(prev => [...prev, data.note]);
                setNewNoteText('');
                setShowAddNote(false);
                
                // Update the file note count in parent component
                if (data.file_update && onUpdateNoteCount) {
                    onUpdateNoteCount(data.file_update.s3_object_id, data.file_update.notes_count);
                }
            } else {
                setError(data.error || 'Failed to add note');
            }
        } catch (err) {
            setError('Error adding note: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleUpdateNote = async (noteId) => {
        if (!editingText.trim()) return;
        
        setIsLoading(true);
        setError('');
        
        try {
            const response = await fetch('https://api.casedb.co/file-notes.php', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: noteId,
                    note_text: editingText.trim()
                }),
                credentials: 'include'
            });
            
            const data = await response.json();
            
            if (data.success) {
                setNotes(prev => prev.map(note => 
                    note.id === noteId ? data.note : note
                ));
                setEditingNoteId(null);
                setEditingText('');
                
                // Update the file note count in parent component if provided
                if (data.file_update && onUpdateNoteCount) {
                    onUpdateNoteCount(data.file_update.s3_object_id, data.file_update.notes_count);
                }
            } else {
                setError(data.error || 'Failed to update note');
            }
        } catch (err) {
            setError('Error updating note: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };
    const handleDeleteNote = async (noteId) => {
        if (!window.confirm('Are you sure you want to delete this note?')) return;
        
        setIsLoading(true);
        setError('');
        
        try {
            const response = await fetch('https://api.casedb.co/file-notes.php', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: noteId }),
                credentials: 'include'
            });
            
            const data = await response.json();
            
            if (data.success) {
                setNotes(prev => prev.filter(note => note.id !== noteId));
                
                // Update the file note count in parent component
                if (data.file_update && onUpdateNoteCount) {
                    onUpdateNoteCount(data.file_update.s3_object_id, data.file_update.notes_count);
                }
            } else {
                setError(data.error || 'Failed to delete note');
            }
        } catch (err) {
            setError('Error deleting note: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const startEditing = (note) => {
        setEditingNoteId(note.id);
        setEditingText(note.text);
        setOpenMenuNoteId(null);
    };

    const cancelEditing = () => {
        setEditingNoteId(null);
        setEditingText('');
    };

    useEffect(() => {
        const handleClickOutside = () => {
            if (openMenuNoteId !== null) {
                setOpenMenuNoteId(null);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [openMenuNoteId]);

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString();
    };

    if (!s3ObjectId) {
        return null;
    }

    return (
        <div className="file-notes">
            <div className="notes-header" style={(notes?.length === 0 || notesMinimized) ? { marginBottom: '0', border: 'none', padding: !showAddNote ? '0' : '' } : {}}>
                <div className="notes-title">
                    <FiMessageSquare size={16} />
                    <span>Notes ({notes.length})</span>
                </div>
                <div className='notes-actions'>
                    <div className='notes-minimizer' onClick={() => setNotesMinimized(!notesMinimized)}>
                        <ChevronDown size={14} style={{ transform: notesMinimized ? 'rotate(0deg)' : 'rotate(180deg)' }} />
                    </div>
                    {!showAddNote && (
                        <button 
                            className="add-note-btn"
                            onClick={() => setShowAddNote(true)}
                            disabled={isLoading}
                        >
                            <FiPlus size={14} />
                            Add Note
                        </button>
                    )}
                </div>
            </div>

            {error && (
                <div className="notes-error">
                    {error}
                </div>
            )}

            {showAddNote && (
                <div className="add-note-form" style={{ marginBottom: notes.length === 0 ? '0' : '' }}>
                    <textarea
                        value={newNoteText}
                        onChange={(e) => setNewNoteText(e.target.value)}
                        placeholder="Add a note about this file..."
                        className="note-textarea"
                        rows={3}
                    />
                    <div className="note-form-actions">
                        <button 
                            className="save-note-btn"
                            onClick={handleAddNote}
                            disabled={isLoading || !newNoteText.trim()}
                        >
                            <FiSave size={14} />
                            Save
                        </button>
                        <button 
                            className="cancel-note-btn"
                            onClick={() => {
                                setShowAddNote(false);
                                setNewNoteText('');
                            }}
                            disabled={isLoading}
                        >
                            <FiX size={14} />
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {isLoading && notes.length === 0 && (
                <div className="notes-loading">
                    Loading notes...
                </div>
            )}

            {!notesMinimized && (
                <div className="notes-list">
                    {notes.map(note => (
                        <div key={note.id} className="note-item">
                            {editingNoteId === note.id ? (
                                <div className="edit-note-form">
                                    <textarea
                                    value={editingText}
                                    onChange={(e) => setEditingText(e.target.value)}
                                    className="note-textarea"
                                    rows={3}
                                />
                                    <div className="note-form-actions">
                                        <button 
                                            className="save-note-btn"
                                            onClick={() => handleUpdateNote(note.id)}
                                            disabled={isLoading || !editingText.trim()}
                                        >
                                            Save
                                        </button>
                                        <button 
                                            className="cancel-note-btn"
                                            onClick={cancelEditing}
                                            disabled={isLoading}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="note-content">
                                    <div className='note-header'>
                                        <div className="note-meta">
                                            <span className="note-author">{note.userName || note.userId}</span>
                                            <span className="note-date">{note.createdAt ? `${formatLastModified(note.createdAt)[4]} ${formatLastModified(note.createdAt)[2]}` : ''}</span>
                                            {note.updatedAt !== note.createdAt && (
                                                <span className="note-edited">(edited {note.updatedAt ? `${formatLastModified(note.updatedAt)[4]} ${formatLastModified(note.updatedAt)[2]}` : ''})</span>
                                            )}
                                        </div>
                                        <div className="note-actions">
                                            <button
                                                className="note-actions-menu-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setOpenMenuNoteId(prev => (prev === note.id ? null : note.id));
                                                }}
                                                disabled={isLoading}
                                                title="Actions"
                                            >
                                                <EllipsisVertical size={16} />
                                            </button>
                                            {openMenuNoteId === note.id && (
                                                <div className="note-actions-menu" onClick={(e) => e.stopPropagation()}>
                                                    <button
                                                        className="note-actions-menu-item delete"
                                                        onClick={() => handleDeleteNote(note.id)}
                                                        disabled={isLoading}
                                                        title="Delete note"
                                                    >
                                                        <Trash size={16} />
                                                    </button>
                                                    <button
                                                        className="note-actions-menu-item"
                                                        onClick={() => startEditing(note)}
                                                        disabled={isLoading}
                                                        title="Edit note"
                                                    >
                                                        <Pencil size={16} />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <p className="note-text">{note.text}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/*notes.length === 0 && !isLoading && !showAddNote && (
                <div className="no-notes">
                    No notes yet. Click "Add Note" to add one.
                </div>
            )*/}
        </div>
    );
};

export default FileNotes;